import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const UpdatePointsModal = ({ member, open, onClose, onSuccess }) => {
  const snackbar = useSnackbarAlert();

  const [transactionSubtotal, setTransactionSubtotal] = useState("");
  const [addPointsReason, setAddPointsReason] = useState("in-store purchase");
  const [pointsToDeduct, setPointsToDeduct] = useState("");
  const [deductionReason, setDeductionReason] = useState("employee error");
  const [addPointsPasscode, setAddPointsPasscode] = useState("");
  const [deductPointsPasscode, setDeductPointsPasscode] = useState("");
  const [addPointsError, setAddPointsError] = useState("");
  const [deductPointsError, setDeductPointsError] = useState("");
  const [confirmLargeSubtotal, setConfirmLargeSubtotal] = useState(false);
  const [lastSubtotalRequest, setLastSubtotalRequest] = useState(null);

  const addPointsReasons = [
    "in-store purchase",
    "promotional",
    "customer appreciation",
    "event participation",
  ];

  const deductionReasons = [
    "employee error",
    "customer return",
    "fraudulent activity",
    "account adjustment",
    "program violation",
    "system_error",
  ];

  const validatePasscode = async (passcode) => {
    if (!passcode) throw new Error("Employee passcode is required.");
    const response = await api.post("/admin/employees/validatePasscode", {
      passcode,
    });
    if (!response.data.valid) {
      throw new Error("Invalid employee passcode.");
    }
  };

  const handleAddPoints = async () => {
    setAddPointsError("");

    try {
      await validatePasscode(addPointsPasscode);

      const subtotal = parseFloat(transactionSubtotal);

      if (!subtotal || isNaN(subtotal) || subtotal <= 0) {
        setAddPointsError("Please enter a valid transaction subtotal.");
        return;
      }

      if (subtotal > 100 && !confirmLargeSubtotal) {
        setLastSubtotalRequest(subtotal);
        setConfirmLargeSubtotal(true);
        return;
      }

      const res = await api.post(
        `/admin/digitalLoyalty/${member._id}/addPoints`,
        {
          subtotal,
          reason: addPointsReason,
          employee: addPointsPasscode,
        }
      );

      const { pointsAdded } = res.data;

      snackbar.success(
        `${pointsAdded} point${
          pointsAdded !== 1 ? "s" : ""
        } added successfully!`
      );

      clearFields();
      onSuccess();
      onClose();
    } catch (err) {
      setAddPointsError(err.message);
    }
  };

  const handleDeductPoints = async () => {
    setDeductPointsError("");
    try {
      await validatePasscode(deductPointsPasscode);

      if (!pointsToDeduct || isNaN(pointsToDeduct) || pointsToDeduct <= 0) {
        setDeductPointsError(
          "Please enter a valid number of points to deduct."
        );
        return;
      }

      if (!deductionReason) {
        setDeductPointsError("Please select a reason for the deduction.");
        return;
      }

      await api.post(`/admin/digitalLoyalty/${member._id}/deductPoints`, {
        points: parseInt(pointsToDeduct, 10),
        reason: deductionReason,
        employee: deductPointsPasscode,
      });

      snackbar.success("Points deducted successfully!");
      clearFields();
      onSuccess();
      onClose();
    } catch (err) {
      setDeductPointsError(err.message);
    }
  };

  const handleSubtotalChange = (value) => {
    const clean = value.replace(/[^\d]/g, "");
    if (clean) {
      const dollars = (parseInt(clean, 10) / 100).toFixed(2);
      setTransactionSubtotal(dollars);
    } else {
      setTransactionSubtotal("");
    }
  };

  const clearFields = () => {
    setTransactionSubtotal("");
    setAddPointsReason("in-store purchase");
    setPointsToDeduct("");
    setDeductionReason("employee error");
    setAddPointsPasscode("");
    setDeductPointsPasscode("");
    setConfirmLargeSubtotal(false);
    setLastSubtotalRequest(null);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
        <Box sx={{ textAlign: "center", mb: 5, fontWeight: "bold" }}>
          <Typography variant="h4" gutterBottom>
            Update Points
          </Typography>
        </Box>

        {/* Add Points Section */}
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: "bold", fontSize: "18px" }}>
          Add Points
        </Typography>
        {addPointsError && (
          <Typography color="error">{addPointsError}</Typography>
        )}
        <TextField
          label="Transaction Subtotal"
          fullWidth
          margin="normal"
          value={transactionSubtotal}
          onChange={(e) => handleSubtotalChange(e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="add-points-reason-label">Reason</InputLabel>
          <Select
            labelId="add-points-reason-label"
            value={addPointsReason}
            onChange={(e) => setAddPointsReason(e.target.value)}>
            {addPointsReasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Employee Passcode (Add Points)"
          type="password"
          fullWidth
          margin="normal"
          value={addPointsPasscode}
          onChange={(e) => setAddPointsPasscode(e.target.value)}
        />

        {confirmLargeSubtotal && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 1,
              color: "#856404",
            }}>
            <Typography fontWeight="bold" sx={{ mb: 1 }}>
              Large Subtotal Warning
            </Typography>
            <Typography sx={{ mb: 2 }}>
              The entered subtotal is ${lastSubtotalRequest.toFixed(2)}, which
              will result in a large number of points. Please confirm this is
              correct before proceeding.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={handleAddPoints}>
              Confirm and Add Points
            </Button>
          </Box>
        )}

        {!confirmLargeSubtotal && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleAddPoints}
            sx={{ mt: 2 }}>
            Add Points
          </Button>
        )}

        <Divider sx={{ mt: 5 }} />

        {/* Deduct Points Section */}
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ mt: 2, fontWeight: "bold", fontSize: "18px" }}>
          Deduct Points
        </Typography>
        {deductPointsError && (
          <Typography color="error">{deductPointsError}</Typography>
        )}
        <TextField
          label="Points to Deduct"
          type="number"
          fullWidth
          margin="normal"
          value={pointsToDeduct}
          onChange={(e) => setPointsToDeduct(e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="deduction-reason-label">Reason</InputLabel>
          <Select
            labelId="deduction-reason-label"
            value={deductionReason}
            onChange={(e) => setDeductionReason(e.target.value)}>
            {deductionReasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Employee Passcode (Deduct Points)"
          type="password"
          fullWidth
          margin="normal"
          value={deductPointsPasscode}
          onChange={(e) => setDeductPointsPasscode(e.target.value)}
        />
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleDeductPoints}
          sx={{ mt: 2 }}>
          Deduct Points
        </Button>
      </Box>
    </Modal>
  );
};

export default UpdatePointsModal;
